import React from 'react';
import type { AppDispatch } from '../store/store';
import type { UploadedFileMeta } from '../store/chatSlice';
import { clearTargetLanguages } from '../store/chatSlice';
import { setSelectedTool } from '../store/chatModeSlice';

/**
 * Options accepted by the useChatInputForm hook.
 */
export interface UseChatInputFormOptions {
  onNavigateToChat?: (message: string, tools?: string[]) => void;
  onHeightChange?: (height: number) => void;
  externalValue?: string;
  onExternalValueApplied?: () => void;
  isStreaming?: boolean;
  selectedTool: string | null;
  effectiveToolForSend: string | null;
  uploadedFiles: UploadedFileMeta[];
  chatPage?: boolean;
  iconSize?: number;
  isTablet: boolean;
  isMobile: boolean;
  isSpeechListening: boolean;
  speechTranscript: string;
  firstToolUsed: string | null;
  setFirstToolUsed: React.Dispatch<React.SetStateAction<string | null>>;
  isCompareActive: boolean;
  setIsCompareActive: React.Dispatch<React.SetStateAction<boolean>>;
  isSummarizeActive: boolean;
  setIsSummarizeActive: React.Dispatch<React.SetStateAction<boolean>>;
  dispatch: AppDispatch;
}

/**
 * Return value of useChatInputForm hook.
 */
export interface UseChatInputFormReturn {
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  handleSubmit: (e: React.FormEvent) => void;
  handleSendClick: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Custom hook that encapsulates form state, auto-resize, external value sync,
 * and submission/keyboard logic previously inline in ChatInput.
 */
export function useChatInputForm(options: UseChatInputFormOptions): UseChatInputFormReturn {
  const {
    onNavigateToChat,
    onHeightChange: _onHeightChange,
    externalValue,
    onExternalValueApplied,
    isStreaming,
    selectedTool,
    effectiveToolForSend,
    uploadedFiles,
    chatPage: _chatPage,
    iconSize: _iconSize,
    isTablet,
    isMobile: _isMobile,
    isSpeechListening,
    speechTranscript,
    firstToolUsed,
    setFirstToolUsed,
    isCompareActive,
    setIsCompareActive,
    isSummarizeActive: _isSummarizeActive,
    setIsSummarizeActive,
    dispatch,
  } = options;

  const [value, setValue] = React.useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // --- Auto-resize textarea ---
  React.useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Step 1: Reset to base state to get clean content measurement
    // Larger base height for tablets (64px vs 56px)
    const baseHeight = isTablet ? 64 : 56;
    textarea.style.height = baseHeight + 'px';
    textarea.style.paddingBottom = '16px';
    textarea.style.overflowY = 'hidden';

    // Step 2: Measure the actual content height
    const scrollHeight = textarea.scrollHeight;
    const contentHeight = Math.max(scrollHeight, baseHeight);
    const maxContentHeight = 128;

    // Step 3: Determine if we need scrolling based on content only
    const needsScrolling = contentHeight > maxContentHeight;
    const finalHeight = needsScrolling ? maxContentHeight : contentHeight;

    // Step 4: Apply settings
    textarea.style.height = finalHeight + 'px';
    textarea.style.overflowY = needsScrolling ? 'auto' : 'hidden';

    // Step 5: Auto-scroll to bottom during voice dictation so the user
    // always sees the most recently spoken words as the ghost text grows.
    if (needsScrolling && isSpeechListening) {
      textarea.scrollTop = textarea.scrollHeight;
    }
  // speechTranscript added: during dictation `value` state is frozen (onChange is a
  // no-op while listening) so without this dep the resize never fires as the live
  // preview grows. Adding it triggers a fresh measurement on every new word spoken.
  }, [value, isTablet, speechTranscript, isSpeechListening]);

  // --- External value synchronization ---
  React.useEffect(() => {
    if (externalValue !== undefined && externalValue !== value && !isSpeechListening) {
      setValue(externalValue);
      // Focus the textarea after setting value
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Move cursor to end of text
        const length = externalValue.length;
        textareaRef.current.setSelectionRange(length, length);
      }
      // Notify parent that external value was applied
      if (onExternalValueApplied) {
        onExternalValueApplied();
      }
    }
  }, [externalValue, value, onExternalValueApplied, isSpeechListening]);

  // --- Form submission ---
  const handleSubmit = React.useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedValue = value.trim();

    // DATA ANALYSIS GATING: When the data_analysis tool is active we require at least one uploaded file
    // before allowing a message to be sent.
    if (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0) return;
    if (!trimmedValue || !onNavigateToChat || isStreaming) return;

    // Track first tool used when conversation starts
    if (!firstToolUsed && effectiveToolForSend) {
      setFirstToolUsed(effectiveToolForSend);
    }

    // If Compare is active and exactly 2 files, send with no tool (not as a tool)
    if (isCompareActive && uploadedFiles.length === 2) {
      setIsCompareActive(false);
      onNavigateToChat(trimmedValue);
      setValue('');
      return;
    }

    // For all other cases, send with the effective tool so model-selected reasoning mode
    // still includes ['reasoning'] even when no explicit tool pill is selected.
    const tools = effectiveToolForSend ? [effectiveToolForSend] : undefined;
    onNavigateToChat(trimmedValue, tools);
    setValue('');
    setIsSummarizeActive(false);
    // Don't clear selectedTool - keep it selected for next message
  }, [
    value,
    selectedTool,
    uploadedFiles,
    onNavigateToChat,
    isStreaming,
    firstToolUsed,
    effectiveToolForSend,
    setFirstToolUsed,
    isCompareActive,
    setIsCompareActive,
    setIsSummarizeActive,
  ]);

  // --- Send button click ---
  const handleSendClick = React.useCallback(() => {
    const trimmedValue = value.trim();

    // Compare mode
    if (isCompareActive && uploadedFiles.length === 2) {
      setIsCompareActive(false);
    }

    // DATA ANALYSIS GATING (manual send button / click path)
    if (selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0) return;
    if (!trimmedValue || !onNavigateToChat || isStreaming) return;

    // Track first tool used when conversation starts
    if (!firstToolUsed && effectiveToolForSend) {
      setFirstToolUsed(effectiveToolForSend);
    }

    // Send with selected tool (if any)
    const tools = effectiveToolForSend ? [effectiveToolForSend] : undefined;
    onNavigateToChat(trimmedValue, tools);
    setValue('');
    setIsSummarizeActive(false);
    // Don't clear selectedTool - keep it selected for next message
  }, [
    value,
    isCompareActive,
    uploadedFiles,
    selectedTool,
    onNavigateToChat,
    isStreaming,
    firstToolUsed,
    effectiveToolForSend,
    setFirstToolUsed,
    setIsCompareActive,
    setIsSummarizeActive,
  ]);

  // --- Keyboard handling ---
  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Block submission while streaming — user can still type but not send
      if (isStreaming) return;
      const form = e.currentTarget.closest('form');
      if (form) {
        // DATA ANALYSIS GATING (keyboard Enter path)
        if (!(selectedTool === 'data_analysis' && uploadedFiles.filter(f => f.fileId).length === 0)) {
          form.requestSubmit();
        }
      }
    }

    // Quick tool shortcuts (Ctrl/Cmd + number)
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
      const shortcuts: { [key: string]: string } = {
        '2': 'web_search',
        '3': 'image_generation',
        '4': 'data_analysis',
        '5': 'translation',
      };

      if (shortcuts[e.key]) {
        e.preventDefault();
        dispatch(setSelectedTool(shortcuts[e.key]));
      }
    }

    // Escape to clear tool selection
    if (e.key === 'Escape' && selectedTool) {
      e.preventDefault();
      dispatch(setSelectedTool(null));
      dispatch(clearTargetLanguages());
    }
  }, [selectedTool, uploadedFiles, dispatch, isStreaming]);

  return {
    value,
    setValue,
    textareaRef,
    handleSubmit,
    handleSendClick,
    handleKeyDown,
  };
}
